import { ForwardedRef, forwardRef, ReactNode, useImperativeHandle, useMemo } from 'react'
import { Range } from 'types'
import { ArtboardLoopRef } from '../types/refs'
import { ArtboardLoopContext } from './context'
import useLoopTime from './hooks/use-loop-time'

type Props = {
    children?: ReactNode
    duration: number
    fps: number
    paused?: boolean
    timing?: Range
}

const ArtboardLoop = (
    { children, duration, fps, paused = false, timing }: Props,
    ref: ForwardedRef<ArtboardLoopRef>
) => {
    const [time, setTime] = useLoopTime(duration, paused, timing)
    const durationInFrames = useMemo(() => Math.round(duration * fps), [duration, fps])

    const progress = useMemo(() => (duration ? time / duration : 0), [duration, time])
    const frame = useMemo(() => Math.floor(durationInFrames * progress), [durationInFrames, progress])

    useImperativeHandle(ref, () => ({
        getProgress: () => {
            return progress
        },
        getTime: () => {
            return time
        },
        goToFrame: (frame: number) => {
            setTime((frame / durationInFrames) * duration)
        },
        goToProgress: (progress: number) => {
            setTime(progress * duration)
        },
        goToTime: (seconds: number) => {
            setTime(Math.max(0, Math.min(duration, seconds)))
        },
    }))

    return <ArtboardLoopContext.Provider value={{ frame, progress }}>{children}</ArtboardLoopContext.Provider>
}

export default forwardRef(ArtboardLoop)
